/**
 * Workout templates: an ordered list of glossary exercises, each with a rest time and a list of target sets. Pure
 * editing helpers (used by the editor and tested) plus small repository functions on a storage `Backend`.
 *
 * Templates refer to exercises as `exercises: [{ exerciseId }]`; `exerciseUsage` in exercises.ts relies on that.
 */
import { asEntry, entryMeasure, foldText, hasVariants, MAX_REST_SEC, setFields, withMeasure, type Exercise, type Variant } from "./exercises";
import { newId, type Backend } from "./storage";

/** normal, warm-up (W), drop set (D), failure (F). Warm-up sets never count for records or trends. */
export const SET_TYPES = ["normal", "warmup", "drop", "failure"] as const;
export type SetType = (typeof SET_TYPES)[number];

/** Target values of one set. Only the fields `setFields()` allows for the exercise are stored. */
export interface TemplateSet {
  type: SetType;
  weightKg?: number;
  reps?: number;
  durationSec?: number;
  distanceM?: number;
}

export interface TemplateExercise {
  exerciseId: string;
  /** the variant this template does the exercise in (todo 16); absent: the glossary's measure */
  measure?: Variant;
  /** rest after each set, in seconds (starts as the exercise's default) */
  restSec: number;
  sets: TemplateSet[];
}

export interface Template {
  id: string;
  name: string;
  notes: string;
  exercises: TemplateExercise[];
}

export type TemplateDraft = Omit<Template, "id"> & { id?: string };

export function newTemplateDraft(): TemplateDraft {
  return { name: "", notes: "", exercises: [] };
}

/**
 * Template sets have the values of `setFields()` as targets. Two of them are optional goals rather than the set's
 * main target (owner, Q10): the reps of a `timed` set (AMRAP: as many as possible, the goal is a guide) and the time
 * of a `distance` set. The editor labels them "Ziel" and lets them be cleared.
 */
export function optionalTargets(e: Pick<Exercise, "measure">) {
  return { reps: e.measure === "timed", duration: e.measure === "distance" };
}

/** The first set of an exercise newly added to a template. */
export function defaultSet(e: Pick<Exercise, "measure">): TemplateSet {
  switch (e.measure) {
    case "reps": return { type: "normal", reps: 10 };
    case "timed": return { type: "normal", durationSec: 40 };
    case "hold": return { type: "normal", durationSec: 30 };
    case "distance": return { type: "normal", distanceM: 20 };
  }
}

export function newTemplateExercise(e: Exercise): TemplateExercise {
  return { exerciseId: e.id, restSec: e.restSec, sets: [defaultSet(e)] };
}

/** Appends the exercises, each with one default set (Strong does the same). */
export function addExercises(d: TemplateDraft, list: Exercise[]): TemplateDraft {
  return { ...d, exercises: [...d.exercises, ...list.map(newTemplateExercise)] };
}

/** "+ Satz": a copy of the last set's values as a normal set, or the default set if there is none. */
export function addSet(te: TemplateExercise, e: Pick<Exercise, "measure">): TemplateExercise {
  const last = te.sets[te.sets.length - 1];
  return { ...te, sets: [...te.sets, last ? { ...last, type: "normal" } : defaultSet(e)] };
}

export function removeSet(te: TemplateExercise, index: number): TemplateExercise {
  return { ...te, sets: te.sets.filter((_, i) => i !== index) };
}

export function updateSet(te: TemplateExercise, index: number, patch: Partial<TemplateSet>): TemplateExercise {
  return { ...te, sets: te.sets.map((s, i) => (i === index ? { ...s, ...patch } : s)) };
}

/** Moves an item to another position (clamped to the list). */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  const target = Math.max(0, Math.min(list.length - 1, to));
  if (from === target || from < 0 || from >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(target, 0, item);
  return next;
}

/** Labels for the set column as in Strong: W, D, F for the special types, normal sets numbered among themselves. */
export function setLabels(sets: TemplateSet[]): string[] {
  let n = 0;
  return sets.map((s) => (s.type === "normal" ? String(++n) : { warmup: "W", drop: "D", failure: "F" }[s.type]));
}

/** Drops the values the exercise does not have (e.g. weight after it became bodyweight) and empty ones. */
export function cleanSet(s: TemplateSet, e: Pick<Exercise, "equipment" | "measure"> | undefined): TemplateSet {
  const f = e ? setFields(e) : { weight: true, reps: true, duration: true, distance: true };
  const out: TemplateSet = { type: s.type };
  if (f.weight && s.weightKg !== undefined) out.weightKg = s.weightKg;
  if (f.reps && s.reps !== undefined) out.reps = s.reps;
  if (f.duration && s.durationSec !== undefined) out.durationSec = s.durationSec;
  if (f.distance && s.distanceM !== undefined) out.distanceM = s.distanceM;
  return out;
}

/** Trims the text and cleans every set against its exercise (in the entry's variant); the result is what gets stored. */
export function cleanTemplate(d: TemplateDraft, exercises: Exercise[]): TemplateDraft {
  const byId = new Map(exercises.map((e) => [e.id, e]));
  return {
    ...d,
    name: d.name.replace(/\s+/g, " ").trim(),
    notes: d.notes.trim(),
    exercises: d.exercises.map((te) => {
      const e = byId.get(te.exerciseId);
      const base: TemplateExercise = { exerciseId: te.exerciseId, restSec: te.restSec, sets: te.sets.map((s) => cleanSet(s, asEntry(e, te))) };
      return withMeasure(te.measure ? { ...base, measure: te.measure } : base, e, te.measure);
    }),
  };
}

/**
 * "Art" (todo 16): the entry done as the other variant. To `timed` every set gets a duration (`durationSec`: the last
 * one used with the exercise, or 40 s) and keeps its reps as Ziel-Reps; to `reps` the durations go. An exercise
 * without variants is left as it is.
 */
export function switchMeasure(te: TemplateExercise, e: Exercise | undefined, to: Variant, durationSec: number): TemplateExercise {
  if (!e || !hasVariants(e) || entryMeasure(e, te) === to) return te;
  const next = withMeasure(te, e, to);
  const target = asEntry(e, next);
  return { ...next, sets: te.sets.map((s) => cleanSet(to === "timed" ? { ...s, durationSec: s.durationSec ?? durationSec } : s, target)) };
}

export type TemplateError = "nameMissing" | "restInvalid" | "valueInvalid";

/** The editor keeps text that does not parse as NaN in the draft (see units.ts), so it cannot be saved. */
export function validateTemplate(d: TemplateDraft): TemplateError[] {
  const errors: TemplateError[] = [];
  if (!d.name.trim()) errors.push("nameMissing");
  if (d.exercises.some((te) => !Number.isInteger(te.restSec) || te.restSec < 0 || te.restSec > MAX_REST_SEC)) errors.push("restInvalid");
  const values = d.exercises.flatMap((te) => te.sets.flatMap((s) => [s.weightKg, s.reps, s.durationSec, s.distanceM]));
  if (values.some((v) => v !== undefined && !(Number.isFinite(v) && v >= 0))) errors.push("valueInvalid");
  return errors;
}

/** "Name (Kopie)", or "Name (Kopie 2)" … if that is taken. The suffix word comes from the UI texts. */
export function copyName(name: string, existing: string[], copy: string): string {
  const taken = new Set(existing.map(foldText));
  for (let i = 1; ; i++) {
    const candidate = `${name} (${copy}${i > 1 ? ` ${i}` : ""})`;
    if (!taken.has(foldText(candidate))) return candidate;
  }
}

/** Alphabetical, German collation (Strong sorts "My Templates" by name too). */
export function sortTemplates(list: Template[]): Template[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }) || a.id.localeCompare(b.id));
}

/**
 * The end time of the last finished session per template. Sessions (todo 3) store `templateId` and `endedAt`
 * (milliseconds since the epoch).
 */
export function lastDoneByTemplate(sessions: { templateId?: string; endedAt?: number }[]): Map<string, number> {
  const last = new Map<string, number>();
  for (const s of sessions) {
    if (!s.templateId || s.endedAt === undefined) continue;
    last.set(s.templateId, Math.max(last.get(s.templateId) ?? 0, s.endedAt));
  }
  return last;
}

// --- storage ---

export const listTemplates = (db: Backend) => db.getAll<Template>("templates");

export type SaveTemplateResult = { ok: true; template: Template } | { ok: false; errors: TemplateError[] };

/** Creates (no id) or updates a template after cleaning and validating it. */
export async function saveTemplate(db: Backend, draft: TemplateDraft, exercises: Exercise[]): Promise<SaveTemplateResult> {
  const d = cleanTemplate(draft, exercises);
  const errors = validateTemplate(d);
  if (errors.length) return { ok: false, errors };
  const template: Template = { ...d, id: d.id ?? newId() };
  await db.put("templates", template);
  return { ok: true, template };
}

/** Stores a copy under a new id and a free "(Kopie)" name. */
export async function duplicateTemplate(db: Backend, id: string, copy: string): Promise<Template | undefined> {
  const t = await db.get<Template>("templates", id);
  if (!t) return undefined;
  const names = (await listTemplates(db)).map((x) => x.name);
  const dup: Template = { ...structuredClone(t), id: newId(), name: copyName(t.name, names, copy) };
  await db.put("templates", dup);
  return dup;
}

/** Sessions started from the template keep its id; they stay readable without it. */
export const deleteTemplate = (db: Backend, id: string) => db.delete("templates", id);
